/**
 * Categoria e cor do DIA do roteiro (teste físico, 13/09/2026, item 28).
 *
 * Cada dia pode receber uma categoria — o "tema" do dia — e uma cor. A
 * categoria é fechada e genérica (nada de marca, parque ou destino: isto vale
 * para qualquer viagem); a cor é uma de oito, escolhida por quem organiza,
 * com a cor sugerida da categoria como ponto de partida.
 */
export const DAY_CATEGORIES = [
  'parques',
  'compras',
  'mercado',
  'entretenimento',
  'show',
  'viagem',
  'esporte',
  'descanso',
  'passeio',
  'outros',
] as const;
export type DayCategory = (typeof DAY_CATEGORIES)[number];

export const DAY_COLORS = ['coral', 'laranja', 'amarelo', 'verde', 'azul', 'violeta', 'rosa', 'cinza'] as const;
export type DayColor = (typeof DAY_COLORS)[number];

export const DAY_CATEGORY_LABELS: Record<DayCategory, { rotulo: string; cor: DayColor }> = {
  parques: { rotulo: 'Parques', cor: 'coral' },
  compras: { rotulo: 'Compras', cor: 'rosa' },
  mercado: { rotulo: 'Mercado', cor: 'verde' },
  entretenimento: { rotulo: 'Entretenimento', cor: 'violeta' },
  show: { rotulo: 'Show', cor: 'laranja' },
  viagem: { rotulo: 'Viagem', cor: 'azul' },
  esporte: { rotulo: 'Esporte', cor: 'verde' },
  descanso: { rotulo: 'Descanso', cor: 'cinza' },
  passeio: { rotulo: 'Passeio', cor: 'amarelo' },
  outros: { rotulo: 'Outros', cor: 'cinza' },
};

export function isDayCategory(v: unknown): v is DayCategory {
  return typeof v === 'string' && (DAY_CATEGORIES as readonly string[]).includes(v);
}

export function isDayColor(v: unknown): v is DayColor {
  return typeof v === 'string' && (DAY_COLORS as readonly string[]).includes(v);
}

/** A cor que vale para o dia: a escolhida, senão a da categoria, senão nenhuma. */
export function dayColorOf(category: DayCategory | null, color: DayColor | null): DayColor | null {
  if (color) return color;
  if (category) return DAY_CATEGORY_LABELS[category].cor;
  return null;
}

/**
 * Período do dia a partir dos horários das atividades: "Manhã", "Manhã /
 * Tarde", "Dia todo". Não é campo: é derivado do que está marcado.
 */
export function dayPeriod(startTimes: readonly string[]): string | null {
  const horas = startTimes.map((t) => Number(t.slice(0, 2))).filter((h) => Number.isFinite(h));
  if (horas.length === 0) return null;
  const periodos = new Set<string>(horas.map((h) => (h < 12 ? 'Manhã' : h < 18 ? 'Tarde' : 'Noite')));
  if (periodos.size === 3) return 'Dia todo';
  return ['Manhã', 'Tarde', 'Noite'].filter((p) => periodos.has(p)).join(' / ');
}
